import axios from "axios";
import type { Feature as FeatureType } from "geojson";
import { FeatureCollection } from "geojson";
import { Feature, MapBrowserEvent, Map as OlMap } from "ol";
import { getCenter } from "ol/extent";
import { GeoJSON } from "ol/format";
import { Point } from "ol/geom";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import WebGLVectorLayer from "ol/layer/WebGLVector";
import { toLonLat } from "ol/proj";
import { XYZ } from "ol/source";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style } from "ol/style";
import { Coordinate } from "openlayers";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FeatureFilterItem } from "./FeatureFilterItem";
import { FEATURE_CHANGES_PROPERTIES } from "./hooks/useFeatureStateSync";
import { useFilteredFeatures } from "./hooks/useFilteredFeatures";
import { useHotkeys } from "./hooks/useHotkeys";
import { useInspectMap } from "./hooks/useInspectMap";
import styles from "./InspectMap.module.scss";
import InspectOverlayView from "./Overlay/InspectOverlayView";
import Swiper from "./Swiper/Swiper";
import { ValidItem } from "./types/inspect";
import {
  FEATURE_STATE,
  FEATURE_STATE_CONVERT,
  FeatureStateConvert,
} from "./types/inspect.type";

type Props = {
  validItem: ValidItem;
  aois: FeatureCollection;
};

export default function InspectMap({ validItem, aois }: Props) {
  const mapRef = useRef<OlMap | null>(null);
  const { requestId, aoiId } = useParams();
  const { initOlMap } = useInspectMap();
  const {
    confirmedFeatureCount,
    incorrectFeatureCount,
    uncertainFeatureCount,
    pendingFeatureCount,
    notDetectionFeatureCount,
    totalCount,
    selectedState,
    filteredFeatures,
    setFeatures,
    setSelectedState,
    setReload,
    handleAddFeature,
    setDeleteId,
  } = useFilteredFeatures();
  const reloadRef = useRef(false);
  const [isShowSwiper, setShowSwiper] = useState(true);
  const [map, setMap] = useState<OlMap | null>(null);
  const [afterLayer, setAfterLayer] = useState<TileLayer<XYZ> | null>(null);
  const [selectedObject, setSelectedObject] = useState<Feature | null>(null);
  const mouseCoordinatesRef = useRef<Coordinate | null>(null);

  const onToggleSwiperHide = () => {
    setShowSwiper(!isShowSwiper);
    if (!isShowSwiper) {
      afterLayer?.setVisible(true);
    } else {
      afterLayer?.setVisible(false);
    }

    map?.render();
  };

  const onToggleAfterTileLayer = (state: boolean) => {
    if (!afterLayer || isShowSwiper) return;
    afterLayer.setVisible(state);
    map?.render();
  };

  const moveToPrevObject = () => {
    if (!filteredFeatures.length) return;

    const currentIndex = filteredFeatures.findIndex(
      (f) => f === selectedObject
    );
    const prevIndex =
      (currentIndex - 1 + filteredFeatures.length) % filteredFeatures.length;
    setSelectedObject(filteredFeatures[prevIndex]);
    moveToFeatureCenter(filteredFeatures[prevIndex]);
  };

  const moveToNextObject = () => {
    if (!filteredFeatures.length) return;

    const currentIndex = filteredFeatures.findIndex(
      (f) => f === selectedObject
    );
    const nextIndex = (currentIndex + 1) % filteredFeatures.length;
    setSelectedObject(filteredFeatures[nextIndex]);
    moveToFeatureCenter(filteredFeatures[nextIndex]);
  };

  const handleZoomInOnFeature = () => {
    if (!selectedObject || !map) return;
    const geometry = selectedObject.getGeometry();
    const extent = geometry?.getExtent();
    if (extent) {
      map.getView().setCenter(getCenter(geometry!.getExtent()));
      map.getView().setZoom(20);
    }
  };

  const handleUpdateStateOnFeature = (state: FeatureStateConvert) => {
    if (!selectedObject) return;
    if (
      selectedObject.get(FEATURE_CHANGES_PROPERTIES.STATE.KEY) ===
      FEATURE_STATE_CONVERT.NOT_DETECTED
    )
      return;
    selectedObject.set(FEATURE_CHANGES_PROPERTIES.STATE.KEY, state);

    const customEvent = new CustomEvent("changeDetectionObjectState", {
      detail: {
        featureId: selectedObject.get("id"),
        state: state,
      },
    });
    window.dispatchEvent(customEvent);
  };

  const handleNewDetection = async () => {
    if (!mouseCoordinatesRef.current) return;
    const [lng, lat] = mouseCoordinatesRef.current;
    try {
      const response = await axios.post<FeatureType>(
        `${process.env.REACT_APP_BASE_URL}/valid/feature/${requestId}`,
        {
          lng,
          lat,
        }
      );

      const newFeature = response.data;

      const format = new GeoJSON();
      const feature = format.readFeature(newFeature, {
        dataProjection: "EPSG:4326",
        featureProjection: "EPSG:3857",
      });

      const findLayer = map
        ?.getLayers()
        .getArray()
        .find((layer) => layer.get("layerType") === "result");

      if (!findLayer) return;

      (findLayer as VectorLayer<VectorSource>)
        .getSource()
        ?.addFeature(feature as Feature);

      handleAddFeature(feature as Feature);
    } catch (e) {}
  };

  useHotkeys([
    {
      code: "KeyH",
      onKeyDown: onToggleSwiperHide,
    },
    {
      code: "Space",
      onKeyDown: () => onToggleAfterTileLayer(true),
      onKeyUp: () => onToggleAfterTileLayer(false),
      preventDefault: true,
    },
    {
      code: "ArrowLeft",
      onKeyDown: moveToPrevObject,
      preventDefault: true,
    },
    {
      code: "ArrowRight",
      onKeyDown: moveToNextObject,
      preventDefault: true,
    },
    {
      code: "KeyF",
      onKeyDown: handleZoomInOnFeature,
    },
    {
      code: "KeyS",
      onKeyDown: () =>
        handleUpdateStateOnFeature(FEATURE_STATE_CONVERT.CONFIRMED),
    },
    {
      code: "KeyD",
      onKeyDown: () => handleUpdateStateOnFeature(FEATURE_STATE_CONVERT.HIDDEN),
    },
    {
      code: "KeyA",
      onKeyDown: () => handleUpdateStateOnFeature(FEATURE_STATE_CONVERT.HOLD),
    },
    {
      code: "KeyE",
      onKeyDown: () => {
        handleNewDetection();
      },
    },
    {
      code: "KeyR",
      onKeyDown: () => {
        handleKeyDownRemoveDetection();
      },
    },
    {
      code: "Enter",
      onKeyDown: () => {
        // handleKeyDownConfirm();
      },
    },
  ]);

  const handleKeyDownConfirm = () => {
    if (!selectedObject) return;
    const id = selectedObject.get("id");
    confirmDetection(id);
    map?.getOverlays().clear();
    moveToNextObject();
  };

  const confirmDetection = async (id: number) => {
    try {
      const findLayer = (window as any).map
        .getLayers()
        .getArray()
        .find((layer: any) => layer.get("layerType") === "result");

      if (!(findLayer instanceof WebGLVectorLayer)) return;

      const findFeature = (findLayer as WebGLVectorLayer<VectorSource>)
        .getSource()
        ?.getFeatures()
        .find((feature) => feature.get("id") === id);

      findFeature?.set(FEATURE_CHANGES_PROPERTIES.CONFIRMED.KEY, "1");

      findLayer.changed();

      await axios.put<any>(
        `${process.env.REACT_APP_BASE_URL}/valid/feature/confirm/${id}`
      );

      reloadRef.current = !reloadRef.current;
      setReload(reloadRef.current);
    } catch (err) {}
  };

  const handleKeyDownRemoveDetection = () => {
    if (!selectedObject) return;
    const id = selectedObject.get("id");
    deleteDetectionObjectState(id);
    map?.getOverlays().clear();
    // setSelectedObject(null);
    moveToNextObject();
  };

  const moveToFeatureCenter = (feature: Feature) => {
    const geometry = feature.getGeometry();
    if (!geometry) return;
    const extent = geometry.getExtent(); // BoundingBox의 범위를 얻음
    const coordinates = [
      (extent[0] + extent[2]) / 2, // X 중앙
      (extent[1] + extent[3]) / 2, // Y 중앙
    ];
    const belowY = extent[1];

    // 지도 중심을 해당 feature의 좌표로 이동
    map?.getView().setCenter(coordinates);
    map?.getOverlays().clear();
    const overlayPosition = [coordinates[0], belowY];

    map?.addOverlay(
      InspectOverlayView({
        feature: feature as Feature,
        position: overlayPosition,
      })
    );
  };

  useEffect(() => {
    const olMap = initOlMap(mapRef.current);
    if (!olMap) return;

    setMap(olMap);

    const beforeLayer = new TileLayer({
      source: new XYZ({
        url: validItem.beforeUrl,
        maxZoom: 21,
      }),
    });

    olMap.addLayer(beforeLayer);

    const afterLayer = new TileLayer({
      source: new XYZ({
        url: validItem.afterUrl,
        maxZoom: 21,
      }),
    });

    setAfterLayer(afterLayer);

    olMap.addLayer(afterLayer);

    const format = new GeoJSON();
    const feature = format.readFeature(validItem.bbox, {
      dataProjection: "EPSG:4326",
      featureProjection: "EPSG:3857",
    }) as Feature;

    const extent = feature.getGeometry()!.getExtent();
    olMap.getView().fit(extent, {
      padding: [50, 50, 50, 50],
    });

    handleRenderAoi(olMap);

    olMap.on("singleclick", handleClickFeatureEvent);
    olMap.on("pointermove", (event) => {
      const [lng, lat] = toLonLat(event.coordinate);
      mouseCoordinatesRef.current = [lng, lat];
    });

    window.addEventListener(
      "changeDetectionObjectState",
      handleChangeDetectionEvent as EventListener
    );
  }, []);

  const handleChangeDetectionEvent = (e: CustomEvent) => {
    if (e.detail.state === "DELETE") {
      deleteDetectionObjectState(e.detail.featureId);
      return;
    }

    updateDetectionObjectState(e.detail.featureId, e.detail.state);
  };

  const deleteDetectionObjectState = async (id: number) => {
    try {
      const findLayer = (window as any).map
        .getLayers()
        .getArray()
        .find((layer: any) => layer.get("layerType") === "result");

      if (!(findLayer instanceof WebGLVectorLayer)) return;

      const findFeature = (findLayer as WebGLVectorLayer<VectorSource>)
        .getSource()
        ?.getFeatures()
        .find((feature) => feature.get("id") === id);

      findLayer.getSource().removeFeature(findFeature);

      findLayer.changed();

      await axios.delete<any>(
        `${process.env.REACT_APP_BASE_URL}/valid/feature/${id}`
      );

      setDeleteId(String(id));
    } catch (err) {}
  };

  const updateDetectionObjectState = async (id: number, state: string) => {
    try {
      await axios.put<any>(
        `${process.env.REACT_APP_BASE_URL}/valid/status/${state}?featureId=${id}`
      );

      reloadRef.current = !reloadRef.current;
      setReload(reloadRef.current);
    } catch (err) {}
  };

  const handleRenderAoi = (olMap: OlMap) => {
    const format = new GeoJSON();
    const aoiFeatures = format.readFeatures(aois, {
      dataProjection: "EPSG:4326",
      featureProjection: "EPSG:3857",
    });

    const findAoi = aoiFeatures.find((feature) => feature.get("id") == aoiId);

    if (!findAoi) return;

    const aoiLayer = new VectorLayer({
      source: new VectorSource({
        features: [findAoi],
      }),
      style: new Style({
        fill: new Fill({
          color: "transparent",
        }),
        stroke: new Stroke({
          color: "#FF0000",
          width: 5,
        }),
      }),
    });

    olMap.addLayer(aoiLayer);

    const extent = findAoi.getGeometry()!.getExtent();
    olMap.getView().fit(extent, {
      padding: [50, 50, 50, 50],
    });

    handleFetchResults(olMap);
  };

  const handleFetchResults = async (olMap: OlMap) => {
    try {
      const response = await axios.get<any>(
        `${process.env.REACT_APP_BASE_URL}/valid/request/${requestId}?aoiId=${aoiId}`
      );

      drawResultLayer(olMap, response.data);
    } catch (e) {}
  };
  const RADIUS_RESOLUTION = [
    13,
    ["*", ["get", "radius"], 0.07],
    14,
    ["*", ["get", "radius"], 0.15],
    15,
    ["*", ["get", "radius"], 0.31],
    16,
    ["*", ["get", "radius"], 0.625],
    17,
    ["*", ["get", "radius"], 1.25],
    18,
    ["*", ["get", "radius"], 2.5],
    19,
    ["*", ["get", "radius"], 5],
    20,
    ["*", ["get", "radius"], 10],
    21,
    ["*", ["get", "radius"], 20],
    22,
    ["*", ["get", "radius"], 40],
    23,
    ["*", ["get", "radius"], 80],
    24,
    ["*", ["get", "radius"], 160],
  ];

  type ValidationStatus =
    | "CORRECT"
    | "INCORRECT"
    | "UNCERTAIN"
    | "NOT_DETECTED"
    | "DEFAULT";
  const colors: Record<ValidationStatus, string> = {
    CORRECT: "#43CA92",
    INCORRECT: "#CCCCCC",
    UNCERTAIN: "#F33B23",
    NOT_DETECTED: "#C623E6",
    DEFAULT: "#EBE52F",
  };

  // Polygon 스타일 함수
  const polygonStyle = (color: string) => ({
    "stroke-color": color,
    "stroke-width": [
      "interpolate",
      ["exponential", 2],
      ["zoom"],
      18,
      2,
      19,
      6,
      21,
      10,
    ],
    "fill-color": "rgba(255, 255, 255, 0.15)",
  });

  // Point 스타일 함수
  const pointStyle = (color: string) => ({
    "circle-radius": [
      "interpolate",
      ["exponential", 2],
      ["zoom"],
      ...RADIUS_RESOLUTION,
    ],
    "circle-stroke-width": 2,
    "circle-stroke-color": color,
    "circle-fill-color": "rgba(255, 255, 255, 0.15)",
  });

  // 스타일 배열 생성 (Polygon + Point)
  const styleArray = Object.entries(colors).flatMap(([status, color]) => [
    {
      filter: [
        "all",
        ["==", ["get", "validation_status"], status],
        ["==", ["geometry-type"], "Point"],
      ],
      style: pointStyle(color),
    },
    {
      filter: [
        "all",
        ["==", ["get", "validation_status"], status],
        [
          "any",
          ["==", ["geometry-type"], "Polygon"],
          ["==", ["geometry-type"], "MultiPolygon"],
        ],
      ],
      style: polygonStyle(color),
    },
  ]);

  const drawResultLayer = (
    map: OlMap,
    featureCollection: FeatureCollection
  ) => {
    const format = new GeoJSON();
    const features = format.readFeatures(featureCollection, {
      dataProjection: "EPSG:4326",
      featureProjection: "EPSG:3857",
    });

    setFeatures(features);

    features.forEach((f) => {
      const status = f.get("validation_status");
      if (
        !["CORRECT", "INCORRECT", "UNCERTAIN", "NOT_DETECTED"].includes(status)
      ) {
        f.set("validation_status", "DEFAULT");
      }
    });

    const vectorSource = new VectorSource({
      features,
    });

    const resultLayer = new WebGLVectorLayer({
      source: vectorSource,
      style: styleArray,
    });

    map.addLayer(resultLayer);
  };

  const handleClickFeatureEvent = (event: MapBrowserEvent) => {
    if (!event.map) return;
    event.map.getOverlays().clear();

    const features = event.map?.getFeaturesAtPixel(event.pixel);

    if (features.length === 0) return;

    const firstPointFeature = features[0];

    // 상태 변경
    setSelectedObject(firstPointFeature as Feature);
    setSelectedState(FEATURE_STATE.TOTAL);

    const geometry = firstPointFeature.getGeometry();

    if (!geometry) return;

    const extent = geometry.getExtent();
    const minX = extent[0];
    const minY = extent[1];
    const maxX = extent[2];

    const position: [number, number] = [(minX + maxX) / 2, minY];

    event.map.addOverlay(
      InspectOverlayView({
        feature: firstPointFeature as Feature,
        position,
      })
    );
    // 레이어 갱신
  };

  const filterList = [
    {
      key: "Confirmed",
      count: confirmedFeatureCount,
      state: FEATURE_STATE.CONFIRMED,
      color: "__confirmed",
    },
    {
      key: "Incorrect",
      count: incorrectFeatureCount,
      state: FEATURE_STATE.HIDDEN,
      color: "__incorrect",
    },
    {
      key: "Uncertain",
      count: uncertainFeatureCount,
      state: FEATURE_STATE.HOLD,
      color: "__uncertain",
    },
    {
      key: "Pending",
      count: pendingFeatureCount,
      state: FEATURE_STATE.PENDING,
      color: "__pending",
    },
    {
      key: "Not Detect",
      count: notDetectionFeatureCount,
      state: FEATURE_STATE.NOT_DETECTED,
      color: "__not-detection",
    },
    {
      key: "TOTAL",
      count: totalCount,
      state: FEATURE_STATE.TOTAL,
      color: "__total",
    },
  ] as const;

  return (
    <>
      {isShowSwiper && map && afterLayer && (
        <>
          <Swiper olMap={map} afterTileLayer={afterLayer} />
        </>
      )}
      <div className={styles["inspect-state__wrapper"]}>
        {filterList.map(({ key, count, state, color }) => (
          <FeatureFilterItem
            key={key}
            labelKey={key}
            count={count}
            selectedState={selectedState}
            currentState={state}
            colorClass={color}
            onClick={() => {
              setSelectedState(state);
            }}
          />
        ))}
      </div>
      <div id="map" className={styles["map"]}></div>
    </>
  );
}
